import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateWineDto, UpdateWineDto, WineDto } from '../../proxy/wines/models';
import { WineService } from '@proxy/wines';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, firstValueFrom, map, Observable, take } from 'rxjs';
import { StorageService } from 'src/app/shared/services/storage.service';

@Component({
  selector: 'app-wine-form',
  templateUrl: './wine-form.component.html',
  standalone: false
})
export class WineFormComponent implements OnInit {
  @Input() wine?: WineDto | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<WineDto>();
  selectedFile: File | null = null;
  imagePreviewUrl: string | null = null;


  form!: FormGroup;
  isEditMode = false;

  wineries$: Observable<{ value: string, label: string }[]>;

  constructor(
    private fb: FormBuilder,
    private wineService: WineService,
    private route: ActivatedRoute,
    private router: Router,
    private storageService: StorageService

  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode = true;
      this.wineService.get(Number(id)).subscribe(wine => {
        this.wine = wine;
        this.tryPatchForm();
      });
    }

    this.wineries$ = this.wineService.getWineries().pipe(
      distinctUntilChanged(),
      map(val => val.map(item => {
        return { label: item.name, value: item.id.toString() };
      }))
    );

    this.wineries$.subscribe(() => this.tryPatchForm());
  }

  onImageSelected(files: File[]): void {
    if (files && files.length > 0) {
      this.selectedFile = files[0];
    }
  }

  private tryPatchForm() {
    if (!this.form || !this.wine) return;

    this.wineries$.pipe(take(1)).subscribe(options => {
      const wineryExists = options.some(o => o.value === this.wine!.wineryId.toString());
      this.form.patchValue({
        ...this.wine,
        wineryId: wineryExists ? this.wine!.wineryId.toString() : null
      });

      // Componente
      if (this.wine!.fileAttachmentId) {
        this.storageService.getImage(this.wine.fileAttachmentId).subscribe(blob => {
          this.imagePreviewUrl = URL.createObjectURL(blob);
        });
      }

    });
  }



  private buildForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      description: [''],
      fileAttachmentId: [''],
      wineryId: [0, [Validators.required]],
      brand: [''],
      varietal: [''],
      type: [''],
      harvest: [''],
      stock: ['', [Validators.required]],
      price: ['', [Validators.required]],
      maxOrderQuantity: [''],
      country: [''],
      isActive: [true],
      externalCode:['']
    });
  }

  private patchForm(wine: WineDto) {
    this.isEditMode = true;
    this.form.patchValue(wine);
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);
    if (control?.invalid && control?.touched) {
      if (control.errors?.['required']) return '::ThisFieldIsRequired';
    }
    return undefined;
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    let newFileAttachmentId: number | null = null;

    if (this.selectedFile) {
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      newFileAttachmentId = await firstValueFrom(
        this.wineService.uploadFileByFile(formData)
      );
    }

    const dto = this.form.value as CreateWineDto | UpdateWineDto;

    dto.fileAttachmentId = newFileAttachmentId ?? this.wine?.fileAttachmentId;
    const oldFileAttachmentId = this.wine?.fileAttachmentId;

    if (this.isEditMode) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.wineService.update(Number(id), dto as UpdateWineDto).subscribe(result => {
          if (newFileAttachmentId && oldFileAttachmentId) {
            this.wineService.deleteFileByFileAttachmentId(oldFileAttachmentId).subscribe();
          }
          this.handleSuccess(result);
        });
      }
    } else {
      this.wineService.create(dto as CreateWineDto).subscribe(result => this.handleSuccess(result));
    }
  }

  onCancel() {
    this.router.navigate(['/wines']);
  }

  private handleSuccess(result: WineDto) {
    this.saved.emit(result);
    this.router.navigate(['/wines']);
  }
}
